interface SparklineProps {
  uniqueKey: string
  points: number[]
  variant?: 'default' | 'tiny'
  label?: string
}

const strokeWidth = 2
const defaultWidth = 120
const tinyWidth = 60
const sparklineHeight = 25

export function Sparkline({uniqueKey, points, variant = 'default', label}: SparklineProps) {
  const width = variant === 'tiny' ? tinyWidth : defaultWidth
  const coordinates = getCoordinates(points, width, sparklineHeight - strokeWidth)

  const gradientId = `gradient-${uniqueKey}`
  const maskId = `sparkline-${uniqueKey}`

  return (
    <div style={{height: sparklineHeight}}>
      <svg
        data-testid={`${uniqueKey}-sparkline`}
        aria-label={label}
        width={width}
        height={sparklineHeight}
        viewBox={`0 0 ${width} ${sparklineHeight}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="1" y2="0">
            <stop offset="0%" stopColor="var(--contribution-default-bgColor-1)" />
            <stop offset="10%" stopColor="var(--contribution-default-bgColor-2)" />
            <stop offset="25%" stopColor="var(--contribution-default-bgColor-3)" />
            <stop offset="50%" stopColor="var(--contribution-default-bgColor-4)" />
          </linearGradient>
          <mask id={maskId} x="0" y="0" width={width} height={sparklineHeight}>
            <polyline
              transform={`translate(0, ${sparklineHeight - strokeWidth / 2}) scale(1,-1)`}
              points={coordinates.join(' ')}
              fill="transparent"
              stroke="#8cc665"
              strokeWidth={strokeWidth}
            />
          </mask>
        </defs>

        <g>
          <rect
            x="0"
            y="0"
            width={width}
            height={sparklineHeight}
            style={{
              stroke: 'none',
              fill: `url(#${gradientId})`,
              mask: `url(#${maskId})`,
            }}
          />
        </g>
      </svg>
    </div>
  )
}

export function getCoordinates(points: number[], width: number, height: number): string[] {
  const scaledPoints = rescale(points.length < 2 ? [0, 0] : points, height)
  const xOffset = width / (scaledPoints.length - 1)

  const coordinates = []
  for (let i = 0; i < scaledPoints.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const y = scaledPoints[i]!
    coordinates.push(`${xOffset * i},${y}`)
  }

  return coordinates
}

/*
 * Scale the y-axis of the graph to fit within the maximum pixel height.
 * Prevents commit counts greater than the pixel height from skewing the
 * graph.
 */
function rescale(points: number[], height: number): number[] {
  const maxPoint = Math.max(...points)
  if (maxPoint <= height) {
    return points
  }
  return points.map(point => roundToTwoDecimalPlaces((point / maxPoint) * height))
}

function roundToTwoDecimalPlaces(value: number) {
  return Math.round(value * 100) / 100
}
